import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
    getTransportModes,
    createTransportModeConfig,
    deleteTransportModeConfig
} from '../../services/firestore/transportModeService';

const TransportModeConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getTransportModes(user.tenantId);
            setModes(data);
        } catch (err) {
            toastError('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setFormData({ tenantId: user.tenantId, name: '', code: '' });
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name || !formData.code) {
            toastError('Lütfen tür adı ve kod giriniz.');
            return;
        }

        try {
            await createTransportModeConfig({
                ...formData,
                code: formData.code.toUpperCase()
            });
            success('Nakliye türü eklendi');
            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError('Hata: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Nakliye Türünü Sil',
            message: 'Bu nakliye türünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteTransportModeConfig(id);
                    success('Silindi');
                    loadData();
                } catch (err) {
                    toastError('Silinemedi');
                }
            }
        });
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h3 className="mb-xs">Nakliye Türü Tanımları</h3>
                    <p className="text-secondary text-sm">
                        Planlamada kullanılacak nakliye türleri (FTL, LTL, Intermodal vb.)
                    </p>
                </div>
                <Button variant="primary" onClick={handleOpenModal}>+ Yeni Nakliye Türü</Button>
            </div>

            <div className="card" style={{ padding: 0 }}>
                {loading ? (
                    <div className="p-lg text-center">Yükleniyor...</div>
                ) : modes.length === 0 ? (
                    <div className="p-lg text-center text-secondary">
                        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🚛</div>
                        Kayıt yok.
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Tür Adı</th>
                                <th>Kod</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {modes.map(m => (
                                <tr key={m.id}>
                                    <td style={{ fontWeight: 600 }}>{m.name}</td>
                                    <td>
                                        <code className="bg-surface-subtle px-sm py-xs rounded font-mono text-xs">
                                            {m.code}
                                        </code>
                                    </td>
                                    <td>
                                        <Button size="sm" variant="danger" onClick={() => handleDelete(m.id)}>Sil</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Yeni Nakliye Türü">
                <div className="form-group">
                    <label className="form-label">Tür Adı</label>
                    <input
                        className="input"
                        value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Örn: Komple (FTL)"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Kod</label>
                    <input
                        className="input"
                        value={formData.code || ''}
                        onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="Örn: FTL, LTL"
                    />
                    <small className="text-secondary">Benzersiz kısa kod.</small>
                </div>
                <div className="flex justify-end gap-md mt-lg">
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default TransportModeConfig;
